/* @flow */
import React, { Component } from 'react';
import { Tooltip } from '@mui/material';
import ListComponent from '../../components/SharedComponents/ListComponent/ListComponent';
import { convertDateTime } from '../../helper-classes/utility-functions';
import { getSplittedHeader } from '../../helper-classes/utility-react-functions';
import {
    getUserPreference,
    saveUserPreference,
    getColumnPreferences,
    getReArrangedTableHeaders,
    getParsedPreferencesValue,
    isUpdatePreference,
    rearrangeColumns,
} from '../../helper-classes/listUtil';
import { getFleetioAssets } from './helper-classes/fleetio-services';
import { FLEETIO_ASSET_LIST_PREF_KEY, FLEETIO_HELP_TEXT } from './constants.fleetio';
import HelpButton from '../../components/SharedComponents/HelpButton/HelpButton';
import AppLoader from '../../components/AppLoader';
import { RECORDS_PER_PAGE } from '../../constants/Config';


export type Props = {
    tableRootClass?: string,
    onEditAssetSync?: Function,
};

export type State = {
    tableData: {
        listHeaders: Array<{show: boolean}>,
        listData: Array<{}>,
        totalRecords: number,
    },
    rowsPerPage: number,
    pageNumber: number,
    orderBy: string,
    order: string,
    isUpdating: boolean,
    showListing: boolean,
};

const DEFAULT_PAGE = { page: 0, rowsPerPage: RECORDS_PER_PAGE };

class FleetioAssetList extends Component<Props, State> {
    userPreferences: Object;

    constructor(props: Props) {
        super(props);
        this.userPreferences = null;
        this.state = {
            tableData: {
                listHeaders: this.getListHeader(),
                listData: [],
                totalRecords: 0,
                isEditable: true,
            },
            rowsPerPage: RECORDS_PER_PAGE,
            pageNumber: 0,
            orderBy: 'name',
            order: 'asc',
            isUpdating: true,
            showListing: false,
        };
    }

    componentDidMount() {
        getUserPreference(this, FLEETIO_ASSET_LIST_PREF_KEY);
    }

    getConnectionStatus = (n) => {
        const color = n.vehicleId ? '#007aff' : '#c62828';
        const label = n.vehicleId ? 'Connected' : 'Not Connected';
        const tooltip = n.vehicleId
            ? FLEETIO_HELP_TEXT.STATUS_CONNECTED
            : FLEETIO_HELP_TEXT.STATUS_NOT_CONNECTED;
        return (
            <Tooltip title={tooltip} disableFocusListener>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ color, fontWeight: 500 }}>{label}</span>
                </div>
            </Tooltip>
        );
    }

    getSyncStatus = (value: ?boolean) => {
        const label = value ? 'Yes' : 'No';
        const color = value ? '#2e7d32' : '#c62828';
        return (
            <Tooltip title={label} disableFocusListener>
                <span style={{ color, fontWeight: 500 }}>{label}</span>
            </Tooltip>
        );
    }

    getListHeader = () => {
        const preferences = getParsedPreferencesValue(this.userPreferences);
        const syncPreferencesColumns = [
            ['odometerSync', 'Odometer Sync'],
            ['engineHoursSync', 'Engine Hours Sync'],
            ['dtcSync', 'DTC Sync'],
        ].map(([field, label]) => ({
            [field]: label,
            ...getColumnPreferences(field, preferences),
            customMarkup: d => this.getSyncStatus(d[field]),
        }));

        const columns = [
            {
                name: 'Name',
                ...getColumnPreferences('name', preferences),
            },
            {
                vin: 'VIN',
                ...getColumnPreferences('vin', preferences),
                customMarkup: d => (d.vin || ''),
            },
            {
                ...getSplittedHeader('Odometer (mi)', '', 'odometer'),
                ...getColumnPreferences('odometer', preferences),
                disableSort: true,
                customMarkup: d => Number.parseFloat(d.odometer || 0).toFixed(1),
            },
            {
                engineHours: 'Engine Hours',
                ...getColumnPreferences('engineHours', preferences),
                disableSort: true,
                customMarkup: d => Number.parseFloat(d.engineHours || 0).toFixed(1),
            },
            {
                lastTelemetrySyncedDate: 'Last Telemetry Synced',
                ...getColumnPreferences('lastTelemetrySyncedDate', preferences),
                customMarkup: d => (d.lastTelemetrySyncedDate
                    ? convertDateTime(d.lastTelemetrySyncedDate)
                    : ''),
            },
            {
                vehicleId: 'Connection Status',
                ...getColumnPreferences('vehicleId', preferences),
                customMarkup: d => this.getConnectionStatus(d),
            },
            ...syncPreferencesColumns,
        ];

        if (preferences) {
            return getReArrangedTableHeaders(columns);
        }
        return columns;
    }

    getData = () => { // NOSONAR
        this.fetchAssets(this.state.pageNumber, this.state.rowsPerPage);
    }

    fetchAssets = (
        pageNumber: number,
        rowsPerPage: number,
        orderBy: string = this.state.orderBy,
        order: string = this.state.order,
    ) => {
        this.setState({ isUpdating: true });
        const offset = pageNumber * rowsPerPage;
        getFleetioAssets(offset, rowsPerPage, orderBy, order)
            .then((data) => {
                const { tableData } = this.state;
                if (data?.content) {
                    tableData.listData = data.content;
                    tableData.totalRecords = data.total || 0;
                    this.setState({ tableData, isUpdating: false });
                } else {
                    tableData.listData = [];
                    tableData.totalRecords = 0;
                    this.setState({ tableData, isUpdating: false });
                }
            });
    }

    loadNextPage = (
        rows: number,
        value: number,
        order: string = this.state.order,
        orderBy: string = this.state.orderBy,
    ) => {
        const updateUserPref = isUpdatePreference(this, { rowsPerPage: value });
        this.setState(
            { pageNumber: rows, rowsPerPage: value, orderBy, order },
            () => {
                if (updateUserPref) {
                    saveUserPreference(this, FLEETIO_ASSET_LIST_PREF_KEY);
                }
            },
        );
        this.fetchAssets(rows, value, orderBy, order);
    }

    refreshList = (page) => {
        const { orderBy, order } = this.state;
        this.fetchAssets(page.page, page.rowsPerPage, orderBy, order);
    }

    listTypeContextAction = (action: string, page = DEFAULT_PAGE) => {
        if (action === 'refresh') this.refreshList(page);
        return true;
    }

    sortTableColumn = (order: string, orderBy: string, { pageNumber, rowsPerPage }: Object) => {
        this.setState({ orderBy, order, pageNumber }, () => {
            saveUserPreference(this, FLEETIO_ASSET_LIST_PREF_KEY);
        });
        this.fetchAssets(pageNumber, rowsPerPage, orderBy, order);
    }

    rearrangeColumns = (listHeaders) => {
        rearrangeColumns(this, listHeaders, { prefKey: FLEETIO_ASSET_LIST_PREF_KEY });
    }

    clickRow = (asset: any) => {
        const { onEditAssetSync } = this.props;
        if (!onEditAssetSync || !asset) return;
        onEditAssetSync(asset);
    }

    render() {
        const {
            tableData, isUpdating, pageNumber, rowsPerPage, orderBy, order, showListing,
        } = this.state;
        const { tableRootClass } = this.props;
        return (
            showListing ? <ListComponent
                tableData={tableData}
                loadNextPage={this.loadNextPage}
                isUpdating={isUpdating}
                listTypeContextAction={this.listTypeContextAction}
                clickRow={this.clickRow}
                rearrangeColumns={this.rearrangeColumns}
                sortTableColumn={this.sortTableColumn}
                order={order}
                orderBy={orderBy}
                tableTitle="Assets"
                permissionKey="Admin"
                tableSubTitle={
                    <HelpButton
                        type="tableHelp"
                        helpText={FLEETIO_HELP_TEXT.TABLE_HELP}
                        style={{ marginTop: '-12px' }}
                        popoverWidth={480}
                    />
                }
                showSearch={false}
                tableButtons={['Refresh']}
                pageNumber={pageNumber}
                rowsPerPage={rowsPerPage}
                resizableHeader
                rootClasses={tableRootClass}
                loaderStyle={{ textAlign: 'center', position: 'sticky', margin: '15vh' }}
                overrideStyle={{
                    tableWrapper: {
                        maxHeight: 'calc(78vh - 180px)',
                        overflowY: isUpdating ? 'hidden' : 'scroll',
                    },
                }}
            /> : <AppLoader type="fullScreen" />
        );
    }
}

export default FleetioAssetList;
